import { useOrganizations } from '@contexts/organizations-provider';
import { updateSelectedOrganization } from '@contexts/organizations-provider/api/update-selected-organization/updateSelectedOrganization';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { SfAccount } from '../../api/types/sfRedirect.types';
import type { UseOrgSelectionReturn } from './useOrgSelection.types';

/**
 * Selects the resolved SF org via `updateSelectedOrganization`.
 *
 * Waits for OrganizationsProvider's initial load (GET /selected-organization)
 * to settle before checking whether a PUT is needed. This avoids a redundant
 * PUT when the org is already selected on the backend.
 *
 * Optimizations:
 * - Waits for initial org load to complete before acting.
 * - Skips PUT if the org is already the selected organization.
 * - Invalidates `['selectedOrganization']` after PUT so OrgRouteGuard
 *   sees the new org on navigation and doesn't fire a redundant PUT.
 * - Invalidates `['organizations']` cache only when the org does NOT
 *   exist in the cached list (import flow — new org needs to appear).
 *
 * The `initialLoadDone` flag is one-way (false → true, never reverts) so
 * that invalidations during the import flow don't trigger effect cleanup
 * and cancel in-flight requests.
 *
 * Context values are read via refs to avoid the effect cleanup cancelling
 * in-flight requests when OrganizationsProvider re-renders.
 */
export function useOrgSelection(
  org: SfAccount | undefined,
  permissionsGranted: boolean | null
): UseOrgSelectionReturn {
  const queryClient = useQueryClient();
  const { organizations, selectedOrganization, isOrganizationsLoading } =
    useOrganizations();
  const [orgReady, setOrgReady] = useState(false);
  const [orgError, setOrgError] = useState(false);
  const orgSelecting = useRef(false);

  // One-way flag: waits for the provider's GET /organizations AND
  // GET /selected-organization to settle so we know whether the org
  // is already selected. Requires organizations.length > 0 to ignore
  // the initial disabled state (where isLoading is false but no data).
  // Never reverts to false — import-flow invalidations won't re-trigger.
  const initialLoadDoneRef = useRef(false);

  if (
    organizations.length > 0 &&
    !isOrganizationsLoading &&
    !initialLoadDoneRef.current
  ) {
    initialLoadDoneRef.current = true;
  }

  // Refs keep current values without re-triggering the effect
  const organizationsRef = useRef(organizations);
  organizationsRef.current = organizations;

  const selectedOrgRef = useRef(selectedOrganization);
  selectedOrgRef.current = selectedOrganization;

  useEffect(() => {
    if (
      !initialLoadDoneRef.current ||
      permissionsGranted !== true ||
      !org?.isOnboarded ||
      orgReady ||
      orgError ||
      orgSelecting.current
    )
      return;

    // Skip PUT if the org is already selected
    if (selectedOrgRef.current?.id === org.organizationId) {
      setOrgReady(true);
      return;
    }

    orgSelecting.current = true;

    let cancelled = false;
    updateSelectedOrganization({ id: org.organizationId })
      .then(() => {
        const invalidations: Promise<void>[] = [
          // Refresh selectedOrganization cache so OrgRouteGuard sees the
          // new org on navigation and doesn't fire a redundant PUT.
          queryClient.invalidateQueries({
            queryKey: ['selectedOrganization'],
          }),
        ];

        // Also refetch the organizations list when the org is not
        // already cached (import flow — newly created org).
        const orgInList = organizationsRef.current.some(
          o => o.id === org.organizationId
        );
        if (!orgInList) {
          invalidations.push(
            queryClient.invalidateQueries({
              queryKey: ['organizations'],
            })
          );
        }

        return Promise.all(invalidations);
      })
      .then(() => {
        if (!cancelled) setOrgReady(true);
      })
      .catch(() => {
        if (!cancelled) setOrgError(true);
      });
    return (): void => {
      cancelled = true;
    };
  }, [
    organizations.length,
    isOrganizationsLoading,
    permissionsGranted,
    org,
    orgReady,
    orgError,
    queryClient,
  ]);

  const resetOrgError = useCallback((): void => {
    setOrgError(false);
    orgSelecting.current = false;
  }, []);

  return { orgReady, orgError, resetOrgError };
}
